import axios from "axios";
import { API_CONFIG } from "../config";

interface TransactionRequest {
  startDate: string;
  endDate: string;
}

interface AddTransactionRequest {
  orderId: string;
  amount: number;
  tipAmount: number;
  requestOrigin: string;
}

interface Transaction {
  id: string;
  originalPaymentIdentifier: string;
  date: string;
  description: string;
  amount: number;
  transType: string;
  requestOrigin: string;
  invoice: string;
  originalInvoice: string;
  card: string;
  entryType: string;
  status: string;
  terminalIdentifier: string;
  errorText: string;
  accountIdentifier: string;
  account: string;
  tip: number;
  authDate: string;
  authCode: string;
  transactionId: string;
  originalTransactionId: string;
}

class TransactionApiClient {
  private static readonly SALE_FILTER_VALUE = "sale";
  private static readonly REFUND_FILTER_VALUE = "refund";

  /**
   * Fetch transactions for a specific business
   */
  static async fetchTransactions(
    businessId: string,
    startDate: string,
    endDate: string
  ): Promise<Transaction[]> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return [];
      }

      if (!businessId) {
        console.log("No business ID provided");
        return [];
      }

      const requestBody: TransactionRequest = {
        startDate,
        endDate,
      };

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TRANSACTIONS(
          businessId
        )}`,
        requestBody,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Transactions response:", response.data);

      // More robust data handling
      const transactionData = Array.isArray(response.data)
        ? response.data
        : response.data?.data
        ? response.data.data
        : [];

      return transactionData.map((transaction: any) => ({
        id: transaction?.paymentIdentifier,
        originalPaymentIdentifier: transaction?.originalPaymentIdentifier,
        date: transaction?.createdDate,
        description:
          transaction?.description ||
          transaction?.orderNumber ||
          transaction?.reference ||
          "N/A",
        amount: parseFloat(
          transaction?.amount || transaction?.totalAmount || 0
        ),
        transType:
          transaction?.transType?.toLowerCase() === "refund"
            ? TransactionApiClient.REFUND_FILTER_VALUE
            : TransactionApiClient.SALE_FILTER_VALUE,
        requestOrigin: transaction?.requestOrigin,
        invoice: transaction?.invoice,
        originalInvoice: transaction?.originalInvoice,
        card: transaction?.card || transaction?.cardNumber || "",
        entryType: transaction?.entryType || transaction?.cardEntryType || "",
        status: transaction?.status || transaction?.transactionStatus || "",
        terminalIdentifier:
          transaction?.terminalIdentifier || transaction?.terminalId || "",
        errorText: transaction?.errorText || transaction?.errorMessage || "",
        accountIdentifier:
          transaction?.accountIdentifier || transaction?.accountId || "",
        account: transaction?.account || transaction?.accountName || "",
        tip: parseFloat(transaction?.tip || transaction?.tipAmount || 0),
        authDate: transaction?.authDate,
        authCode: transaction?.authCode || "",
        transactionId: transaction?.transactionId || "",
        originalTransactionId: transaction?.originalTransactionId || "",
      }));
    } catch (error: any) {
      console.error("Error fetching transactions:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return [];
    }
  }

  /**
   * Add a new transaction
   */
  static async addTransaction(
    accountId: string,
    terminalId: string,
    transactionData: AddTransactionRequest
  ): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        throw new Error("No authentication token found");
      }

      if (!accountId || !terminalId) {
        throw new Error("Account ID and Terminal ID are required");
      }

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADD_TRANSACTION(
          accountId,
          terminalId
        )}`,
        transactionData,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      console.log("Add transaction response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error adding transaction:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }
}

export default TransactionApiClient;
